import {
    FileSearchIcon,
    FileTextIcon,
    Loader2,
    PencilLine,
    Trash,
    X,
} from "lucide-react";
import NoPreviewCard from "../card/no-preview-card";
import MedicationsTable from "../tables/medications-table";
import MedicationRequestsTable from "../tables/medication-requests-table";
import RefillHistoryTable from "../tables/refill-history";
import { PatientMedicalPlan, PatientSummaryData, PatientMedication, PrescriptionMedicationData } from "../../api/types";
import { Button } from "@workspace/web-ui/components/button";
import { useState, useEffect } from "react";
import { usePrescriptionModalStore } from "../../store/prescription-modal-store";

// ✅ Add these component imports
import { MultiStepDialog } from "../../../../components/multi-step-dialog";
import { Input } from "@workspace/web-ui/components/input";
import {
    Select,
    SelectTrigger,
    SelectContent,
    SelectItem,
    SelectValue,
} from "@workspace/web-ui/components/select";
import { Textarea } from "@workspace/web-ui/components/textarea";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel,
} from "@workspace/web-ui/components/alert-dialog";

// ✅ Add hook
import { usePrescription } from "../../hooks/use-prescription";
import { PrescriptionModal } from "../modals/prescription-modal";
import { VITAMIN_OPTIONS } from "~/features/dashboard/components/tasks/task-form";
import { useSendActionNeededMutation, useDeletePrescriptionMutation } from "../../api/mutations";

interface MedicationProps {
    prescribedMedicationData?: PatientMedication;
    isLoading?: boolean;
    patient: PatientMedicalPlan | null;
    userId: string;
    activeMedicationData?: PatientMedication;
    requestsMedicationData?: PatientMedication;
    historyMedicationData?: PatientMedication;
    medicalPlanData?: PatientMedicalPlan;
    patientData?: PatientSummaryData;
    program: string;
}

interface DosageOption {
    dosage: string;
    price: number;
}

type MedicationKey = "Compounded Semaglutide" | "Compounded Tirzepatide" | "Zepbound (Branded Tirzepatide)";

// type MedicationHistoryEntry = {
//     medication: string;
//     date: string;
//     status: string;
// };

const MedicationView = ({
    // patient,
    // onDeleteClick,
    activeMedicationData,
}: {
    patient: PatientMedicalPlan | null;
    onDeleteClick: (prescriptionId?: string, encounterId?: string) => void;
    activeMedicationData?: PatientMedication;
}) => (
    <div className="mt-4">
        <div className="w-full bg-[#EBF0FB] p-4 rounded-md flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 relative">
            <div className="flex flex-col space-y-1 flex-1">
                <div className="flex items-center gap-2 text-base font-medium text-[#1A202C]">
                    <span>{activeMedicationData?.medical_plan?.medication?.name}</span>
                    {/* <span className="text-sm text-gray-600">•</span>
                    <span className="text-[#1A202C] font-semibold">${activeMedicationData?.data[0]?.cost}</span> */}
                </div>
                {/* <p className="text-sm text-gray-600">
                    Dosage: {patient?.program_details.plan.medication.dosage}
                </p> */}
                {/* <p className="text-sm text-gray-600">Duration: 3 days</p> */}
                <p className="text-sm text-gray-600">Vitamin: {activeMedicationData?.medical_plan?.vitamin?.name}</p>
                {/* <p className="text-sm text-gray-600">
                    Instructions: Inject subcutaneously in the abdomen
                </p> */}
            </div>
            {/* <div className="flex items-center gap-4 sm:justify-end w-full sm:w-auto">
                <button
                    onClick={() => onDeleteClick(
                        activeMedicationData?.data?.[0]?.prescription_id,
                        activeMedicationData?.medical_plan?.encounter?.encounter_id
                    )}
                    className="text-[#4272DD] text-sm hover:underline flex items-center gap-1"
                >
                    <Trash className="w-4 h-4" />
                    Delete
                </button>
            </div> */}
        </div>
    </div>
);

const NoMedicationView = ({
    medication,
    vitamin,
    medicationReviewed,
    handleAddMedication,
    isVerified,
    setOpenDeleteDialog,
    paidStatus,
}: {
    medication: string;
    vitamin: string;
    medicationReviewed: boolean;
    patientData: PatientSummaryData;
    isVerified: boolean;
    handleAddMedication: () => void;
    setOpenDeleteDialog: (open: boolean) => void;
    paidStatus: string;
    activeMedicationData?: PatientMedication;
}) => (
    <div className="w-full flex flex-col sm:flex-row gap-4 bg-[#EBF0FB] p-4 rounded-[8px] mt-2">
        <div className="flex flex-col space-y-1 flex-1">
            <p className="text-[#464B55] text-xs">Selected Medication:</p>
            <p className="text-[#1A202C] text-sm font-medium">{medication || "--"}</p>
        </div>
        <div className="flex flex-col space-y-1 flex-1">
            <p className="text-[#464B55] text-xs">Selected Vitamin:</p>
            <p className="text-[#1A202C] text-sm font-medium">{vitamin || "--"}</p>
        </div>
        {(paidStatus === "paid" && isVerified) && (
            !medicationReviewed ? (
                <Button
                    className="text-white bg-[#4272DD] w-full sm:w-fit rounded-[8px] shadow-none border border-none hover:bg-[#4272DD]"
                    onClick={handleAddMedication}
                >
                    <PencilLine className="w-4 h-4 mr-2" />
                    <p>Add medication</p>
                </Button>
            ) : (
                <div className="flex gap-2 w-full border sm:w-fit">
                    <Button
                        variant="outline"
                        className="text-[#4272DD] hover:text-[#4272DD] bg-transparent border-none shadow-none p-0 hover:bg-transparent"
                        onClick={() => setOpenDeleteDialog(true)}
                    >
                        <Trash />
                        <p>Delete</p>
                    </Button>
                </div>
            )
        )}

    </div>
);


export const Medication = ({
    activeMedicationData,
    prescribedMedicationData,
    requestsMedicationData,
    historyMedicationData,
    // medicalPlanData,
    patient,
    userId,
    patientData,
    program
}: MedicationProps) => {
    const refillRequestsData: PrescriptionMedicationData[] = requestsMedicationData?.data ?? [];

    const currentMedicationsData: PrescriptionMedicationData[] = Array.isArray(activeMedicationData?.data)
        ? (activeMedicationData?.data as PrescriptionMedicationData[])
        : [];

    // const refillHistoryData: MedicationHistoryEntry[] = Array.isArray(historyMedicationData?.data)
    //     ? (historyMedicationData?.data as MedicationHistoryEntry[])
    //     : [];

    // const [reviewMode, setReviewMode] = useState(true);
    const [openReviewModal, setOpenReviewModal] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [durationError, setDurationError] = useState("");
    const [medicationToDelete, setMedicationToDelete] = useState<{ taskId: string; encounterId: string } | null>(null);

    const [medication, setMedication] = useState(
        activeMedicationData?.medical_plan?.medication?.name || ""
    );
    const [medicationReviewed, setMedicationReviewed] = useState(false);

    // Sync medication state when activeMedicationData loads/changes
    useEffect(() => {
        if (activeMedicationData?.medical_plan?.medication?.name) {
            setMedication(activeMedicationData.medical_plan.medication.name);
        }
    }, [activeMedicationData?.medical_plan?.medication?.name]);

    // ✅ Hook state
    const [newMedication, setNewMedication] = useState(medication);
    const [justification, setJustification] = useState("");
    const [dosage, setDosage] = useState("");
    const [price, setPrice] = useState("");
    const [duration, setDuration] = useState("");
    const [instructions, setInstructions] = useState("");
    const [refills, setRefills] = useState("");
    // Add vitamin state
    const [vitamin, setVitamin] = useState("");

    const medicationOptions = [
        "Compounded Semaglutide",
        "Compounded Tirzepatide",
        "Zepbound (Branded Tirzepatide)",
    ];

    const isVerified = !!patientData?.glp1_guide_accepted && !!patientData?.identity_verified;

    const { handleSubmit } = usePrescription(activeMedicationData?.medical_plan?.encounter?.encounter_id || "");
    const { openPrescriptionModal, setOpenPrescriptionModal } =
        usePrescriptionModalStore();
    const { mutate: sendActionNeeded, isPending: sendActionpending } = useSendActionNeededMutation();
    const { mutate: deletePrescription, isPending: isDeleting } = useDeletePrescriptionMutation();
    
    // Function to send a reminder to the patient to complete verification
    const handleSendReminder = async () => {
        try {
            await sendActionNeeded({ patient_id: userId });
            // You can show a toast or notification here if needed
        } catch (error) {
            // Handle error, e.g., show error toast
        }
    };

    const medicationPricing = {
        "Compounded Semaglutide": [
            { dosage: "0.25 mg", price: 199.99 },
            { dosage: "0.5 mg", price: 249.99 },
            { dosage: "0.75 mg", price: 299.99 },
            { dosage: "1 mg", price: 349.99 },
            { dosage: "12.5 mg", price: 550 },
            { dosage: "15 mg", price: 600 },
        ],
        "Compounded Tirzepatide": [
            { dosage: "2.5 mg", price: 249.99 },
            { dosage: "5 mg", price: 299.99 },
            { dosage: "7.5 mg", price: 399.99 },
            { dosage: "10 mg", price: 499.99 },
            { dosage: "12.5 mg", price: 550 },
            { dosage: "15 mg", price: 600 },
        ],
        "Zepbound (Branded Tirzepatide)": [
            { dosage: "2.5 mg", price: 349.99 },
            { dosage: "5 mg", price: 499.99 },
            { dosage: "7.5 mg", price: 599.99 },
            { dosage: "10 mg", price: 699.99 },
            // { dosage: "12.5 mg", price: 550 },
            // { dosage: "15 mg", price: 600 },
        ]
    };

    const getAvailableDosages = (medication: string): DosageOption[] => {
        return medicationPricing[medication as MedicationKey] || [];
    };

    const getPriceForDosage = (medication: string, dosage: string): string => {
        const options = medicationPricing[medication as MedicationKey] || [];
        const option = options.find(opt => opt.dosage === dosage);
        return option ? option.price.toString() : "";
    };

    // function formatFullDate(dateString: string): string | null {
    //     try {
    //         const date = new Date(dateString);
    //         if (isNaN(date.getTime())) return null;
    //         return date.toLocaleDateString("en-US", {
    //             year: "numeric",
    //             month: "long",
    //             day: "numeric",
    //         });
    //     } catch (error) {
    //         return null;
    //     }
    // }

    const paymentStatus = activeMedicationData?.medical_plan?.payment_status ?? "";
    const isPaid = paymentStatus === "paid";

    const handleAddMedication = () => {
        setOpenPrescriptionModal(true);
    };

    if (openPrescriptionModal) {
        return (
            <PrescriptionModal
                open={openPrescriptionModal}
                patientId={userId}
                encounterId={activeMedicationData?.medical_plan?.encounter?.encounter_id}
                onOpenChange={setOpenPrescriptionModal}
            />
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="rounded-lg border shadow w-full bg-white">
                {/* <div className="p-4 border-b flex items-center justify-between">
                    <h2 className="text-lg font-medium">Medication Plan</h2>
                    {isPaid && (
                        <Button
                            className="bg-[#4272DD] text-white px-4 py-2 rounded-md hover:bg-[#365DC9]"
                            onClick={handleAddMedication}
                        >
                            + Add medication
                        </Button>
                    )}
                </div> */}
                <div className="p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="font-medium">Medication Plan</h3>
                        </div>
                        {isPaid && (
                            isVerified ? (
                                prescribedMedicationData?.data && prescribedMedicationData.data.length > 0 ? (
                                    <Button
                                        className="text-white bg-[#4272DD] w-fit rounded-[8px] shadow-none border border-none hover:bg-[#4272DD]"
                                        onClick={handleAddMedication}
                                    >
                                        <PencilLine className="w-4 h-4 mr-2" />
                                        <p>Add medication</p>
                                    </Button>
                                ) : null
                            ) : (
                                <Button
                                    className="text-white bg-[#4272DD] w-fit rounded-[8px] shadow-none border border-none hover:bg-[#4272DD]"
                                    onClick={handleSendReminder}
                                    disabled={sendActionpending}
                                >
                                    {sendActionpending && (
                                        <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                        </svg>
                                    )}
                                    <p>Send verification reminder</p>
                                </Button>
                            )
                        )}


                    </div>
                    {prescribedMedicationData?.data && prescribedMedicationData.data.length > 0 ? (
                        <MedicationView
                            patient={patient}
                            onDeleteClick={(prescriptionId, encounterId) => {
                                if (prescriptionId && encounterId) {
                                    setMedicationToDelete({ taskId: prescriptionId, encounterId });
                                }
                                setOpenDeleteDialog(true);
                            }}
                            activeMedicationData={activeMedicationData}
                        />
                    ) : (
                        <NoMedicationView
                            medication={activeMedicationData?.medical_plan?.medication?.name ?? ""}
                            vitamin={activeMedicationData?.medical_plan?.vitamin?.name ?? ""}
                            medicationReviewed={medicationReviewed}
                            handleAddMedication={handleAddMedication}
                            setOpenDeleteDialog={setOpenDeleteDialog}
                            patientData={patientData!}
                            isVerified={isVerified}
                            paidStatus={paymentStatus}
                            activeMedicationData={activeMedicationData}
                        />
                    )}

                </div>
                {prescribedMedicationData?.data && prescribedMedicationData.data.length > 0 ? (
                    prescribedMedicationData.data.map((med, idx) => (
                        <div className="p-4" key={idx}>
                            <div className="w-full bg-[#EBF0FB] p-4 rounded-md flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 relative">
                                <div className="flex flex-col space-y-1 flex-1">
                                    <div className="flex items-center gap-2 text-base font-medium text-[#1A202C]">
                                        <span>{med.medication.name}</span>
                                        <span className="text-sm text-gray-600">•</span>
                                        <span className="text-[#1A202C] font-semibold">
                                            ${med.cost}
                                        </span>
                                    </div>
                                    {/* Add vitamin display if available */}
                                    <p className="text-sm text-gray-600">
                                        Vitamin: {med.medication.vitamin?.name ?? ""}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        Dosage: {med.medication.dosage}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        Duration: {med.medication.duration}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        Instructions: {med.medication.instructions}
                                    </p>
                                </div>
                                <div className="flex items-center gap-4 sm:justify-end w-full sm:w-auto">
                                    <button
                                        onClick={() => {
                                            const encounterId = activeMedicationData?.medical_plan?.encounter?.encounter_id;
                                            if (encounterId) {
                                                setMedicationToDelete({ 
                                                    taskId: prescribedMedicationData?.medical_plan?.encounter?.recommended_tasks[0]?.task_id || "", 
                                                    encounterId 
                                                });
                                            }
                                            setOpenDeleteDialog(true);
                                        }}
                                        className="text-[#4272DD] text-sm hover:underline flex items-center gap-1"
                                    >
                                        <Trash className="w-4 h-4" />
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    // an empty state with all values as --
                    <div className="w-full p-4 rounded-md flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 relative">
                        <div className="w-full bg-[#EBF0FB] p-4 rounded-md flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 relative">
                            <div className="flex flex-col space-y-1 flex-1">
                                <div className="flex items-center gap-2 text-base font-medium text-[#1A202C]">
                                    <span>--</span>
                                    <span className="text-sm text-gray-600">•</span>
                                    <span className="text-[#1A202C] font-semibold">
                                        $--
                                    </span>
                                </div>
                                {/* Add vitamin display if available */}
                                <p className="text-sm text-gray-600">
                                    Vitamin: --
                                </p>
                                <p className="text-sm text-gray-600">
                                    Dosage: --
                                </p>
                                <p className="text-sm text-gray-600">
                                    Duration: --
                                </p>
                                <p className="text-sm text-gray-600">
                                    Instructions: --
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>



            {/* Medication Requests Table */}
            {refillRequestsData && refillRequestsData.length > 0 ? (
                <MedicationRequestsTable
                    data={refillRequestsData.map((request: PrescriptionMedicationData) => ({
                        id: request.refill_id,
                        medication: request.medication?.name ?? "",
                        price: typeof request.cost === 'number' && !isNaN(request.cost) ? `$${Number(request.cost).toFixed(2)}` : '$--',
                        requested_date: request.request_datetime,
                        status: request.delivery_status,
                        dosage: request.medication?.dosage ?? "",
                        duration: request.medication?.duration ?? "",
                        refills: request.medication?.refills ?? 0,
                        instructions: request.medication?.instructions ?? "",
                    }))}
                    cardTitle={"Refill Requests"}
                    patientId={userId}
                    program={program}
                />
            ) : (
                <NoPreviewCard
                    Icon={FileSearchIcon}
                    title={"Nothing Yet"}
                    description={"The Patient Currently Has no active refill requests."}
                    cardTitle={"Medication Requests"}
                />
            )}

            {/* Current Medications */}
            {currentMedicationsData.length > 0 ? (
                <MedicationsTable
                    data={currentMedicationsData.map((prescription: PrescriptionMedicationData) => ({
                        medication: prescription.medication.name ?? "",
                        price: prescription.cost.toString(),
                        prescribed_date: prescription.prescribed_on,
                        refills: prescription.medication.refills.toString(),
                        status: prescription.status,
                    }))}
                    cardTitle={"Current Medications"}
                />
            ) : (
                <NoPreviewCard
                    Icon={FileTextIcon}
                    title={"Nothing Yet"}
                    description={"The Patient Currently Has no current medications."}
                    cardTitle={"Current Medications"}
                />
            )}

            {/* Medication Refill History */}
            {historyMedicationData && historyMedicationData?.data && historyMedicationData.data.length > 0 ? (
                <RefillHistoryTable
                    data={historyMedicationData?.data.map((prescription: PrescriptionMedicationData) => ({
                        medication: prescription.medication?.name ?? "",
                        date: prescription.delivery_date || "",
                        status: prescription.refill_status ?? "",
                    }))}
                    cardTitle={"Medication Refill History"}
                />
            ) : (
                <NoPreviewCard
                    Icon={FileTextIcon}
                    title={"Nothing Yet"}
                    description={"The Patient Currently Has no refill history"}
                    cardTitle={"Medication Refill History"}
                />
            )}

            {/* ✅ MultiStepDialog */}
            <MultiStepDialog
                open={openReviewModal}
                onOpenChange={setOpenReviewModal}
                initialData={{
                    medication: medication,
                    newMedication: newMedication,
                    reason: justification,
                    dosage: dosage,
                    price: price,
                    refills: refills,
                    duration: duration,
                    instructions: instructions,
                    vitamin: vitamin, // add vitamin to initialData
                }}
                steps={[
                    {
                        title: `Edit medication - ${patient?.patient_summary.full_name || "Patient"
                            }`,
                        content: (formData, setFormData) => (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs mb-1">
                                        Current Medication:
                                    </label>
                                    <div className="bg-gray-100 rounded px-3 py-2 text-sm">
                                        {formData.medication}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs mb-1">New Medication</label>
                                    <Select
                                        value={formData.newMedication}
                                        onValueChange={(val) =>
                                            setFormData((f) => ({ ...f, newMedication: val }))
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select medication" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {medicationOptions.map((option) => (
                                                <SelectItem key={option} value={option}>
                                                    {option}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {/* Vitamin field */}
                                {/* <div>
                                    <label className="block text-xs mb-1">Vitamin</label>
                                    <Input
                                        value={formData.vitamin}
                                        onChange={(e) =>
                                            setFormData((f) => ({ ...f, vitamin: e.target.value }))
                                        }
                                        placeholder="e.g. Vitamin B12"
                                    />
                                </div> */}
                                <div>
                                    <label className="block text-xs mb-1">
                                        Medical Justification:
                                    </label>
                                    <Textarea
                                        value={formData.reason}
                                        onChange={(e) =>
                                            setFormData((f) => ({ ...f, reason: e.target.value }))
                                        }
                                        placeholder="Enter medical reason for medication change"
                                        maxLength={200}
                                    />
                                    <div className="text-xs text-right">
                                        {formData.reason?.length || 0}/200
                                    </div>
                                </div>
                            </div>
                        ),
                        validate: (formData) =>
                            !!formData.newMedication && !!formData.reason,
                    },
                    {
                        title: `Edit medication - ${patient?.patient_summary.full_name || "Patient"}`,
                        content: (formData, setFormData) => (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs mb-1">Dosage</label>
                                    <Select
                                        value={formData.dosage}
                                        onValueChange={(selectedDosage) => {
                                            // Auto-populate price when dosage is selected
                                            const autoPrice = getPriceForDosage(formData.newMedication, selectedDosage);
                                            setFormData((f) => ({
                                                ...f,
                                                dosage: selectedDosage,
                                                price: autoPrice
                                            }));
                                        }}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select dosage" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {getAvailableDosages(formData.newMedication).map((option) => (
                                                <SelectItem key={option.dosage} value={option.dosage}>
                                                    {option.dosage}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex gap-2">
                                    <div className="flex-1">
                                        <label className="block text-xs mb-1">Price</label>
                                        <Input
                                            value={formData.price}
                                            onChange={(e) =>
                                                setFormData((f) => ({ ...f, price: e.target.value }))
                                            }
                                            placeholder="$"
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-xs mb-1">Duration</label>
                                        <div className="relative">
                                            <Input
                                                value={formData.duration}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    const isNumber = /^\d*$/.test(value);
                                                    setFormData((f) => ({ ...f, duration: value }));
                                                    setDurationError(isNumber ? '' : 'Duration must be a number');
                                                }}
                                                placeholder="e.g. 30"
                                                type="text"
                                                inputMode="numeric"
                                                className={`pr-12 ${durationError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                                            />
                                            <span className="absolute top-1/2 right-4 -translate-y-1/2 text-sm text-gray-500">Days</span>
                                        </div>
                                        {durationError && <p className="text-xs text-red-500 mt-1">{durationError}</p>}
                                    </div>
                                </div>
                                {formData.newMedication !== "Zepbound (Branded Tirzepatide)" && (
                                    <div>
                                        <label className="block text-xs mb-1">Vitamin</label>
                                        <Select
                                            value={formData.vitamin}
                                            onValueChange={(val) =>
                                                setFormData((f) => ({ ...f, vitamin: val }))
                                            }
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select vitamin" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {VITAMIN_OPTIONS.map((vitamin) => (
                                                    <SelectItem key={vitamin} value={vitamin}>
                                                        {vitamin}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-xs mb-1">
                                        Instructions for medication
                                    </label>
                                    <Textarea
                                        value={formData.instructions}
                                        onChange={(e) =>
                                            setFormData((f) => ({
                                                ...f,
                                                instructions: e.target.value,
                                            }))
                                        }
                                        placeholder="Provide instructions for the patient"
                                    />
                                </div>
                            </div>
                        ),
                        validate: (formData) =>
                            !!formData.dosage &&
                            !!formData.price &&
                            !!formData.duration &&
                            !durationError &&
                            !!formData.instructions,
                    }
                ]}
                onComplete={async (formData) => {
                    const num = Number(formData.duration);
                    const formattedDuration = !isNaN(num) && formData.duration.trim() !== ''
                        ? `${num} ${num === 1 ? 'day' : 'days'}`
                        : formData.duration;
                    try {
                        await handleSubmit({
                            medication: formData.newMedication,
                            dosage: formData.dosage,
                            duration: formattedDuration,
                            cost: Number(formData.price) || 0,
                            note: formData.instructions,
                            refills: Number(formData.refills) || 0,
                            reason: formData.reason,
                            vitamin: {
                                name: formData.vitamin
                            }, // pass vitamin
                            substitute: true,
                            patient_id: userId ?? ""
                        });
                        setMedication(formData.newMedication);
                        setVitamin(formData.vitamin); // set vitamin
                        setMedicationReviewed(true);
                        setOpenReviewModal(false);
                    } catch (error) {
                        // Error is already handled by the hook (toast shown)
                    }
                }}
            />

            {/* ✅ Delete Medication Modal */}
            <AlertDialog open={openDeleteDialog} onOpenChange={setOpenDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogCancel asChild>
                        <Button
                            variant="ghost"
                            className="absolute right-4 top-4 h-6 w-6 p-0"
                        >
                            <X className="h-4 w-4" />
                            <span className="sr-only">Close</span>
                        </Button>
                    </AlertDialogCancel>
                    <div className="mt-10 text-center w-full flex items-center border-b">
                        <p className="w-full mx-auto text-[#2D2F35] font-medium text-[20px] md:w-[70%]">
                            Are you sure you want to delete this medication? This action
                            cannot be undone.
                        </p>
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel asChild>
                            <Button 
                                className="rounded-[10px] h-[46px]" 
                                variant="outline"
                                onClick={() => {
                                    setMedicationToDelete(null);
                                }}
                            >
                                Cancel
                            </Button>
                        </AlertDialogCancel>
                        <AlertDialogAction asChild>
                            <Button
                                className="rounded-[10px] h-[46px]"
                                disabled={isDeleting}
                                onClick={() => {
                                    if (medicationToDelete) {
                                        // Delete medication from API using the task endpoint
                                        deletePrescription(
                                            { 
                                                task_id: medicationToDelete.taskId,
                                                encounter_id: medicationToDelete.encounterId,
                                                patient_id: userId,
                                                program: program 
                                            },
                                            {
                                                onSuccess: () => {
                                                    setMedicationToDelete(null);
                                                    setOpenDeleteDialog(false);
                                                },
                                                onError: () => {
                                                    setMedicationToDelete(null);
                                                    setOpenDeleteDialog(false);
                                                }
                                            }
                                        );
                                    } else {
                                        // Just clear local state for locally reviewed medication
                                        setMedicationReviewed(false);
                                        setNewMedication("");
                                        setJustification("");
                                        setDosage("");
                                        setPrice("");
                                        setDuration("");
                                        setRefills("");
                                        setInstructions("");
                                        setVitamin("");
                                        setOpenDeleteDialog(false);
                                    }
                                }}
                            >
                                {isDeleting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    "Confirm"
                                )}
                            </Button>
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default Medication;
